import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { patientQueryKeys, patientsApi } from "../../../shared/api/patients";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export function TagsPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: patientQueryKeys.tags(patientId),
    queryFn: () => patientsApi.listTags(patientId),
  });

  const [draft, setDraft] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (list.data) setDraft(list.data.tags);
  }, [list.data]);

  const save = useMutation({
    mutationFn: (tags: string[]) => patientsApi.replaceTags(patientId, tags),
    onSuccess: (res) => {
      setDraft(res.tags);
      qc.invalidateQueries({ queryKey: patientQueryKeys.tags(patientId) });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function addTag() {
    const v = input.trim();
    if (!v) return;
    if (v.length > 40) {
      setError("Mỗi tag tối đa 40 ký tự");
      return;
    }
    if (draft.includes(v)) {
      setError("Tag đã tồn tại");
      return;
    }
    setError(null);
    setDraft([...draft, v]);
    setInput("");
  }

  function removeTag(t: string) {
    setError(null);
    setDraft(draft.filter((d) => d !== t));
  }

  const dirty =
    list.data &&
    (draft.length !== list.data.tags.length ||
      draft.some((d) => !list.data!.tags.includes(d)));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>Tag (nhãn)</CardTitle>
        {dirty && (
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
          >
            {save.isPending ? "Đang lưu…" : "Lưu"}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {list.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
        {list.isError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {apiError(list.error).detail}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {draft.length === 0 && (
            <span className="text-sm text-muted-foreground">Chưa có tag nào.</span>
          )}
          {draft.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => removeTag(t)}
              title="Bấm để xoá"
              className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            >
              {t}
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Nhập tag rồi Enter (VD: VIP, BHYT, khám định kỳ)"
            maxLength={40}
            className="flex-1"
          />
          <Button type="button" variant="secondary" onClick={addTag}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Thêm
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
